import _ from "lodash";

import {
  ADD_CREATIVE,
  ADD_CREATIVES,
  ADD_DIMENSIONS,
  EDIT_CREATIVE,
  REMOVE_CREATIVE,
  SELECT_CREATIVE,
  DESELECT_CREATIVE,
  CLEAR_SELECTED_CREATIVES,
  SET_WEIGHT_DISTRIBUTION,
  SET_ROTATION_TYPE,
  WEIGHT_OPTIONS,
  ROTATION_TYPES,
  SET_ASSIGNED_PLACEMENTS,
  ADD_DCM_CREATIVES,
  REORDER_CREATIVES,
  SET_START_DATE,
  SET_END_DATE,
  SET_HARD_CUTOFF,
  SET_CREATIVE_API_ERROR,
  ADD_BACKUP,
  RESET_CREATIVE_STATE,
  SET_DYNAMIC_TARGETING_KEYS
} from "../../actions/app/constants";

import dayjs from "dayjs";
import dayOfYear from "dayjs/plugin/dayOfYear";
dayjs.extend(dayOfYear);

const defaultState = {
  byId: {},
  allIds: [],
  selectedId: "",
  weightDistribution: WEIGHT_OPTIONS.EVEN,
  rotationType: ROTATION_TYPES.WEIGHTED,
  assignedPlacements: [],
  startDate: dayjs(),
  endDate: dayjs().dayOfYear(366).set("hour", 23).set("minute", 59),
  hardCutoff: false,
  dynamicTargetingKeys: [],
  selectedIds: [],
  apiError: { error: new Error(), isError: false }
};

export const creatives = (state = defaultState, action) => {
  let nextState = [];
  let nextStateById = _.defaultsDeep({}, state.byId);
  let nextStateAllIds = [...state.allIds];

  switch (action.type) {
    case ADD_CREATIVES:
    case ADD_DCM_CREATIVES:
      nextState = [...action.creatives, ...Object.values(state.byId)]; //convert normalized data to array of all creatives

      if (state.weightDistribution === WEIGHT_OPTIONS.EVEN) {
        nextState = calculateEvenWeights(nextState); //evenly distribute weights across creatives
      }
      let { byId, allIds } = normalizeData(nextState);

      return {
        ...state,
        byId: { ...byId },
        allIds: [...allIds],
        assignedPlacements: [...state.assignedPlacements]
      };
    case ADD_CREATIVE:
      nextState = [...Object.values(state.byId), action.creative]; //convert normalized data to array of all creatives
      if (state.weightDistribution === WEIGHT_OPTIONS.EVEN) {
        nextState = calculateEvenWeights(nextState); //evenly distribute weights across creatives
      }

      let normalizedData = normalizeData(nextState);

      return {
        ...state,
        byId: { ...normalizedData.byId },
        allIds: [...normalizedData.allIds],
        assignedPlacements: [...state.assignedPlacements],
        formState: { ...action.taxonomy }
      };
    case ADD_BACKUP:
      let nextById = {
        ...state.byId,
        [action.backupCreative.id]: { ...action.backupCreative }
      };
      action.primaryIds.map((primaryId) => {
        nextById[primaryId].backupId = action.backupCreative.id;
        return null;
      });

      return {
        ...state,
        byId: nextById,
        allIds: [...state.allIds, action.backupCreative.id],
        assignedPlacements: [...state.assignedPlacements],
        formState: { ...action.taxonomy }
      };
    case REMOVE_CREATIVE:
      let creativeToRemove = nextStateById[action.id];
      //handle removal of backups / association
      if (creativeToRemove.creativeRole === "BACKUP") {
        nextStateAllIds.map((id) => {
          let c = nextStateById[id];
          if (c.backupId === action.id) delete c.backupId;
          return null;
        });
      } else {
        if (creativeToRemove.backupId) {
          let backupAssociated = false;
          Object.values(nextStateById).map((c) => {
            if (
              c.backupId === creativeToRemove.backupId &&
              c.id !== creativeToRemove.id
            )
              backupAssociated = true;
          });

          if (!backupAssociated) {
            delete nextStateById[creativeToRemove.backupId];
            nextStateAllIds = nextStateAllIds.filter(
              (id) => id !== creativeToRemove.backupId
            );
          }
        }
      }

      delete nextStateById[action.id]; // remove creative with id

      nextStateAllIds = nextStateAllIds.filter((id) => id !== action.id); //remove id from ids list

      if (
        state.weightDistribution === WEIGHT_OPTIONS.EVEN &&
        state.allIds.length > 1
      ) {
        //Handle weight recalculation if distribution is even
        let stateArray = calculateEvenWeights(Object.values(nextStateById));
        let normalizedData = normalizeData(stateArray);

        nextStateById = normalizedData.byId;
        nextStateAllIds = normalizedData.allIds;
      }

      return {
        ...state,
        byId: nextStateById,
        allIds: nextStateAllIds,
        assignedPlacements: [...state.assignedPlacements],
        selectedId: state.selectedId === action.id ? "" : state.selectedId
      };
    case EDIT_CREATIVE:
      if (action.creative.id) {
        return {
          ...state,
          byId: { ...state.byId, [action.creative.id]: action.creative },
          allIds: [...state.allIds]
        };
      } else {
        return { ...state };
      }
    case SELECT_CREATIVE:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        selectedId: action.id,
        selectedIds: [...state.selectedIds, action.id]
      };
    case DESELECT_CREATIVE:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        selectedId: "",
        selectedIds: [...state.selectedIds].filter((id) => id !== action.id)
      };
    case CLEAR_SELECTED_CREATIVES:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        selectedId: "",
        selectedIds: []
      };
    case SET_WEIGHT_DISTRIBUTION:
      if (state.allIds.length > 0) {
        let stateArray = [];

        if (action.weightDistribution === WEIGHT_OPTIONS.EVEN) {
          stateArray = calculateEvenWeights(Object.values({ ...state.byId }));
        }

        if (action.weightDistribution === WEIGHT_OPTIONS.CUSTOM) {
          stateArray = setZeroWeights(Object.values({ ...state.byId }));
        }

        let normalizedData = normalizeData(stateArray);
        nextStateById = normalizedData.byId;
        nextStateAllIds = normalizedData.allIds;
      }
      return {
        ...state,
        byId: nextStateById,
        allIds: nextStateAllIds,
        assignedPlacements: [...state.assignedPlacements],
        weightDistribution: action.weightDistribution
      };
    case SET_ROTATION_TYPE:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        rotationType: action.rotationType
      };
    case SET_ASSIGNED_PLACEMENTS:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: action.assignedPlacements
      };
    case REORDER_CREATIVES:
      let nextIds = [...state.allIds];
      nextIds.splice(action.source, 1);
      nextIds.splice(action.destination, 0, action.id);
      return {
        ...state,
        byId: { ...state.byId },
        allIds: nextIds,
        assignedPlacements: [...state.assignedPlacements]
      };
    case SET_START_DATE:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        startDate: dayjs(action.startDate)
      };
    case SET_END_DATE:
      let endDate = dayjs(action.endDate);
      if (!action.timeSpecified) {
        endDate = endDate.endOf("day");
      }
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        endDate: endDate
      };
    case SET_HARD_CUTOFF:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        hardCutoff: action.hardCutoff
      };
    case SET_CREATIVE_API_ERROR:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        apiError: { isError: action.isError, error: action.error }
      };
    case ADD_DIMENSIONS:
      let creativeToAddDimensions = nextStateById[action.id];
      creativeToAddDimensions.dimensions = action.dimensions;
      creativeToAddDimensions.taxonomy.dimensions = action.dimensions
        .split(" ")
        .join("");

      let returnObj = { ...state.byId, [action.id]: creativeToAddDimensions };

      if (action.primaryIds) {
        for (let i = 0; i < action.primaryIds.length; i++) {
          let primaryCreativeToAddDimensions =
            nextStateById[action.primaryIds[i]];
          primaryCreativeToAddDimensions.dimensions = action.dimensions;
          primaryCreativeToAddDimensions.taxonomy.dimensions = action.dimensions
            .split(" ")
            .join("");
          returnObj[action.primaryIds[i]] = primaryCreativeToAddDimensions;
        }
      }

      return {
        ...state,
        byId: returnObj,
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements]
      };
    case SET_DYNAMIC_TARGETING_KEYS:
      return {
        ...state,
        dynamicTargetingKeys: action.keys
      };
    case RESET_CREATIVE_STATE:
      return _.defaultsDeep({}, defaultState);
    default:
      return state;
  }
};

const normalizeData = (dataArray) => {
  let byId = {};
  let allIds = [];

  for (let i = 0; i < dataArray.length; i++) {
    let c = dataArray[i];
    byId[c.id] = { ...c }; // insert into byId object as { "[creative-id]": [creative] }
    allIds.push(c.id); // add id to the list of ids
  }

  return { byId, allIds };
};

const calculateEvenWeights = (dataArray) => {
  if (dataArray.length === 0) return [];
  let totalWeight = 0;
  let numberOfBackups = 0;

  for (let i = 0; i < dataArray.length; i++) {
    if (dataArray[i].creativeRole === "BACKUP") numberOfBackups++;
  }

  let stateArray = dataArray.map((c) => {
    if (c.creativeRole === "PRIMARY") {
      let weight = Math.floor(100 / (dataArray.length - numberOfBackups));
      totalWeight += weight;
      return {
        ...c,
        weight
      };
    } else {
      return { ...c, weight: 0 };
    }
  });

  let difference = Math.abs(100 - totalWeight);

  if (difference !== 0) {
    for (let i = 0; i < difference; i++) {
      let currentCreative = stateArray[i];
      if (currentCreative.creativeRole === "PRIMARY") {
        currentCreative.weight += 1;
      }
    }
  }

  return stateArray;
};

const setZeroWeights = (dataArray) => {
  let stateArray = dataArray.map((c) => {
    return { ...c, weight: 0 };
  });

  return stateArray;
};
